import "./styles/fonts.css";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/core";
import { loadableReady } from "@loadable/component";
import App from "core-web/components/App";
import { INITIAL_PROMISES_CONTEXT, inProduction } from "core-web/constants";
import { translationLocaleFor } from "core-web/constants/translation";
import { PromisesProvider } from "core-web/contexts/promises-context";
import resolveApp from "core-web/state/models/Application/dispatches/resolveApp";
import getCurrentHref from "core-web/state/models/Application/utils/getCurrentHref";
// Setup Sentry for client in production
import { initSentry } from "core-web/utils/sentry";
import { initTranslation } from "core-web/utils/translation";
import React, { useEffect } from "react";
import { CookiesProvider } from "react-cookie";
import ReactDOM from "react-dom";
import { HelmetProvider } from "react-helmet-async";
import { Provider as ReduxProvider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { unregisterServiceWorker } from "serviceWorker";
// @todo: the line below could be used when creating the store, google tag manager and stuff, check royal web for inspiration
// import providers from 'middlewares/providers';
// prettier-ignore
import store from 'core-web/state/store';

initSentry();
unregisterServiceWorker();

const initClient = async (resolve, _reject) => {
    const cache = createCache();

    let promisesContext = { promises: {}, responses: {} };

    if (typeof window !== "undefined" && window[INITIAL_PROMISES_CONTEXT]) {
        // take default from data we saved in SSR
        promisesContext = window[INITIAL_PROMISES_CONTEXT];
        delete window[INITIAL_PROMISES_CONTEXT];
    }

    try {
        const state = store.getState();

        if (!state.application.applicationId) {
            const currentHref = getCurrentHref();
            await resolveApp(currentHref, {}, null);
        }
    } catch (error) {
        console.error("Resolve app error in client", error);

        return resolve();
    }

    const reduxState = store.getState();
    const locale = reduxState.application.locale;
    const translationLocale = translationLocaleFor(locale);
    const translation = await import(/* webpackChunkName: "[request]" */ `locales/${translationLocale}.json`);

    initTranslation({
        lng: translationLocale,
        resources: {
            [translationLocale]: {
                translation,
            },
        },
    });

    loadableReady(() => {
        const AppWithCallbackAfterRender = () => {
            useEffect(() => {
                resolve();
            });

            return (
                <PromisesProvider value={promisesContext}>
                    <CacheProvider value={cache}>
                        <CookiesProvider>
                            <ReduxProvider store={store}>
                                <HelmetProvider>
                                    <BrowserRouter basename={(reduxState?.application?.path || "").replace(/\/+$/, "")}>
                                        <App />
                                    </BrowserRouter>
                                </HelmetProvider>
                            </ReduxProvider>
                        </CookiesProvider>
                    </CacheProvider>
                </PromisesProvider>
            );
        };

        const container = document.getElementById("root");
        if (inProduction) {
            ReactDOM.hydrate(<AppWithCallbackAfterRender />, container);
        } else {
            ReactDOM.render(<AppWithCallbackAfterRender />, container);
        }
    });
};

export default new Promise((resolve, reject) => {
    return initClient(resolve, reject);
});
